import type { AuditEvent } from '../../api/types';

/**
 * The word a row is read by — which is not always the word in its `action`.
 *
 * A current cluster writes **one** name for every policy decision it takes:
 * `policy.decision`, twenty-five kinds of decision under it, with the schema
 * action it decided (`connect`, `kubeRequest`, `dbQuery`) moved into the
 * detail. A column printing `action` for those rows prints the same word down
 * the whole page, and the screen whose entire subject is decisions becomes the
 * one screen that cannot tell them apart.
 *
 * So a decision is read by its schema action, and every other row by its own
 * name.
 *
 * **The row says which it is; nothing here knows the tenancy's version.** The
 * control plane already resolved `cedar_action` for both epochs — out of the
 * detail on a current cluster, out of the `action` column on an older one where
 * the schema action *was* the column — so its presence is the whole test, and
 * on an old row it resolves to the word that was in the column anyway. The
 * absence is just as literal: a row that is not a decision has no schema
 * action, including the refusal for a resource nothing routes to, where no
 * policy was ever asked.
 */
export function actionWord(event: AuditEvent): string {
  return event.cedar_action ?? event.action;
}

/**
 * The chain's own name for the event, when the word above is not it.
 *
 * `undefined` when the two are the same, which is every row of an older
 * tenancy and every non-decision row of a current one. It exists so that a
 * decision read as `connect` still says somewhere that the chain sealed it
 * under `policy.decision` — a panel that quietly replaced one name with the
 * other would be showing a word that is not in the row.
 */
export function eventName(event: AuditEvent): string | undefined {
  return event.cedar_action === undefined || event.cedar_action === event.action
    ? undefined
    : event.action;
}
